/* eslint-disable no-unused-vars */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, useRef } from 'react';
import { TextField, Checkbox, CircularProgress } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LoadingButton } from '@mui/lab';

import Page from '../../../components/Page';
import subscriptionServices from '../../../services/subscriptionServices';
import adminServices from '../../../services/adminServices';
import ErrorFocus from '../../../components/ErrorFocus/ErrorFocus';
import SnackBar from '../../../components/snackbar/snackbar';

import { SectionCard, FieldLabel, AssessmentTypeCard, BackLink, PageHeading, SearchBox } from './components/shared/SharedComponents';
import { ASSESSMENT_TYPES } from './components/shared/inputSx';

const CheckboxList = ({ items, selected, onToggle, emptyMsg, search, onSearchChange, searchPlaceholder, loading, idKey = 'id', labelKey = 'title', subKey }) => (
  <>
    <div className="mb-2">
      <SearchBox value={search} onChange={onSearchChange} placeholder={searchPlaceholder} />
    </div>
    <div className="border border-gray-200 rounded-[7px] max-h-[200px] overflow-y-auto">
      {loading ? (
        <div className="flex justify-center py-6"><CircularProgress size={20} sx={{ color: '#2563eb' }} /></div>
      ) : items.length === 0 ? (
        <div className="flex justify-center py-6"><p className="text-[13px] text-gray-400">{emptyMsg}</p></div>
      ) : items.map((item, idx) => {
        const id = item[idKey] || item.chapter_id || idx;
        const label = item[labelKey] || item.chapter_description || '';
        const sub = subKey ? (item[subKey] || item.chapter_type || '') : '';
        const checked = selected.includes(id);
        return (
          <div key={id} className="flex items-center px-[14px] py-[10px] hover:bg-gray-50"
            style={{ borderBottom: idx < items.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
            <Checkbox size="small" checked={checked} onChange={onToggle(id)}
              sx={{ p: '4px', mr: '10px', color: '#d1d5db', '&.Mui-checked': { color: '#2563eb' } }} />
            <div>
              <p className="text-[13px] font-medium text-gray-900">{label}</p>
              {sub && <p className="text-[11px] text-gray-400 uppercase tracking-[0.04em]">{sub}</p>}
            </div>
          </div>
        );
      })}
    </div>
  </>
);

const CreateB2BPlan = () => {
  const submitModeRef = useRef('draft');
  const navigate = useNavigate();
  const location = useLocation();
  const [assessments, setAssessments] = useState([]);
  const [assessmentSearch, setAssessmentSearch] = useState('');
  const [courses, setCourses] = useState([]);
  const [courseSearch, setCourseSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, title: '' });
  const fromTab = location.state?.fromTab ?? 3;

  useEffect(() => { fetchContent(); }, []);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const [aRes, cRes] = await Promise.all([
        adminServices.getGenericAssessmentDropDown(),
        adminServices.getLookUpData(['course']),
      ]);
      if (aRes.ok) setAssessments(aRes.data || []);
      if (cRes.ok) setCourses((cRes.data.course || []).filter((c) => c.status === 1));
    } catch (e) { console.log(e); }
    setLoading(false);
  };

  
  const filteredAssessments = assessments.filter((a) => {
    const audienceOk = a.audience === 'B2B' || a.audience === 'BOTH' && a.status_type === 1;
    return audienceOk && (a.title || a.chapter_description || '').toLowerCase().includes(assessmentSearch.toLowerCase());
  });
  const filteredCourses = courses.filter((c) => {
    const code = (c.audienceCode || '').toUpperCase();
    return (code === 'B2B' || code === 'BOTH') && (c.title || '').toLowerCase().includes(courseSearch.toLowerCase());
  });

  const initialValues = {
    planName: '', description: '', maxAttempts: 10,
    selectedAssessments: [], selectedCourses: [],
  };

  const validationSchema = Yup.object().shape({
    planName: Yup.string().trim().min(3, 'Minimum 3 characters').required('Required'),
    selectedAssessments: Yup.array().test(
      'at-least-one-content',
      'Please select at least 1 assessment or 1 course',
      (value, context) => {
        const { selectedCourses } = context.parent;
        return (value && value.length > 0) || (selectedCourses && selectedCourses.length > 0);
      }
    ),
    selectedCourses: Yup.array().test(
      'at-least-one-content',
      'Please select at least 1 assessment or 1 course',
      (value, context) => {
        const { selectedAssessments } = context.parent;
        return (value && value.length > 0) || (selectedAssessments && selectedAssessments.length > 0);
      }
    ),
  });

  const handleSubmit = async (values, publishMode = 'draft') => {
    if (submitting) return;
    setSubmitting(publishMode);
    try {
      const fd = new FormData();
      fd.append('name', values.planName);
      fd.append('description', values.description || '');
      fd.append('planType', 'b2b');
      // fd.append('scope', 'b2b');
      fd.append('subscriptionPlan', 'Platform-wide');
      fd.append('subscriptionType', 'B2B');
      fd.append('isOnline', publishMode === 'publish' ? 'true' : 'false');
      fd.append('isPaid', 'false');
      fd.append('frequency', '1');
      // fd.append('maxAttempts', String(values.maxAttempts));
      fd.append('courseIds', JSON.stringify(values.selectedCourses.map(Number)));
      fd.append('assessmentIds', JSON.stringify(values.selectedAssessments.map(Number)));

      const res = await subscriptionServices.postB2BTemplatePlan(fd);
      if (res.ok) {
        setSnackbar({ open: true, title: publishMode === 'publish' ? 'Plan published!' : 'Draft saved' });
        setTimeout(() => navigate(`/app/subscription?tab=${fromTab}`), 1200);
      } else {
        setSnackbar({ open: true, title: res.data?.message || 'Something went wrong' });
      }
    } catch (e) {
      console.log(e);
      setSnackbar({ open: true, title: 'Something went wrong' });
    }
    setSubmitting('');
  };

  return (
    <Page title="Create B2B Plan">
      <div className="max-w-[720px] mx-auto px-4 md:px-8 py-6">
        <BackLink label="Back to Plans" onClick={() => navigate(`/app/subscription?tab=${fromTab}`)} />
        <PageHeading title="Create B2B Plan" subtitle="Define a platform-wide B2B plan. Assign it to tenants from their Plans tab." />

        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(v) => handleSubmit(v, submitModeRef.current)}>
          {({ values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched }) => {
            // const toggleAssessment = (id) => () => setFieldValue('selectedAssessments',
            //   values.selectedAssessments.includes(id) ? values.selectedAssessments.filter((i) => i !== id) : [...values.selectedAssessments, id]);
            // const toggleCourse = (id) => () => setFieldValue('selectedCourses',
            //   values.selectedCourses.includes(id) ? values.selectedCourses.filter((i) => i !== id) : [...values.selectedCourses, id]);
            const toggleAssessment = (id) => () => {
              setFieldValue('selectedAssessments',
                values.selectedAssessments.includes(id)
                  ? values.selectedAssessments.filter((i) => i !== id)
                  : [...values.selectedAssessments, id]);
              setFieldTouched('selectedAssessments', true, false);
              setFieldTouched('selectedCourses', true, false);
            };
            const toggleCourse = (id) => () => {
              setFieldValue('selectedCourses',
                values.selectedCourses.includes(id)
                  ? values.selectedCourses.filter((i) => i !== id)
                  : [...values.selectedCourses, id]);
              setFieldTouched('selectedCourses', true, false);
              setFieldTouched('selectedAssessments', true, false);
            };
            return (
              <Form>
                <ErrorFocus />

                {/* 1 — Plan Identity */}
                <SectionCard step={1} title="Plan Identity" subtitle="Name and describe this B2B plan.">
                  <div className="flex flex-col gap-[18px]">
                    <div>
                      <FieldLabel required>Plan name</FieldLabel>
                      <TextField fullWidth name="planName" placeholder="e.g. Akash Standard" value={values.planName} onChange={handleChange} onBlur={handleBlur} error={Boolean(touched.planName && errors.planName)} helperText={touched.planName && errors.planName} size="small" />
                    </div>
                    <div>
                      <FieldLabel>Description</FieldLabel>
                      <TextField fullWidth multiline rows={3} name="description" placeholder="Briefly describe what this plan provides to the tenant's learners..." value={values.description} onChange={handleChange} onBlur={handleBlur} size="small" />
                    </div>
                  </div>
                </SectionCard>

                {/* 2 — Plan Scope (locked) */}
                <SectionCard step={2} title="Plan Scope" subtitle="B2B plans are always platform-wide. This cannot be changed.">
                  <div className="inline-flex items-center gap-[10px] px-[14px] py-[9px] rounded-[7px] border-2 border-blue-600 bg-blue-50">
                    <p className="text-[13px] font-semibold text-blue-700">Platform-wide</p>
                    <p className="text-[13px] text-gray-500">All assessment types and categories included</p>
                  </div>
                </SectionCard>

                {/* 3 — Pricing (info only) */}
                <SectionCard step={3} title="Pricing" subtitle="B2B plan pricing is managed per-tenant via the Contract tab.">
                  <div className="flex items-start gap-[10px] bg-gray-50 border border-gray-200 rounded-lg px-4 py-[14px]">
                    <InfoOutlinedIcon sx={{ fontSize: '16px', color: '#9ca3af', mt: '1px', flexShrink: 0 }} />
                    <p className="text-[13px] text-gray-700 leading-relaxed">
                      B2B plans are billed via tenant contracts (ARR). No price is stored on the plan itself. Set and manage billing in the tenant&apos;s{' '}
                      <span className="font-semibold">Contract tab</span>.
                    </p>
                  </div>
                </SectionCard>

                {/* 4 — Access Rules */}
                <SectionCard step={4} title="Access Rules" subtitle="B2B plans grant access to all assessment types. Set the attempt limit.">
                  <div className="mb-5">
                    <div className="flex gap-3 mb-2">
                      {ASSESSMENT_TYPES.map((at) => (
                        <AssessmentTypeCard key={at.key} title={at.label} subtitle={at.subtitle} selected disabled />
                      ))}
                    </div>
                    <p className="text-[12px] text-gray-400">All types included — not configurable</p>
                  </div>
                </SectionCard>

                {/* 5 — Content */}
                <SectionCard step={5} title="Content" subtitle="Optionally add assessments and courses now. You can always add more from the plan detail page.">
                  <p className="text-[12px] font-bold text-gray-500 uppercase tracking-[0.06em] mb-[10px]">Assessments</p>
                  <CheckboxList
                    loading={loading}
                    items={filteredAssessments} selected={values.selectedAssessments} onToggle={toggleAssessment}
                    emptyMsg="No assessments available" search={assessmentSearch}
                    onSearchChange={(e) => setAssessmentSearch(e.target.value)}
                    searchPlaceholder="Search assessments..."
                    labelKey="title" subKey="type"
                  />
                  <p className="text-[12px] font-bold text-gray-500 uppercase tracking-[0.06em] mt-5 mb-[10px]">Courses</p>
                  <CheckboxList
                    loading={loading}
                    items={filteredCourses} selected={values.selectedCourses} onToggle={toggleCourse}
                    emptyMsg="No courses available" search={courseSearch}
                    onSearchChange={(e) => setCourseSearch(e.target.value)}
                    searchPlaceholder="Search courses..."
                    labelKey="title" subKey="courseType"
                  />
                  {(touched.selectedAssessments || touched.selectedCourses) &&
                    errors.selectedAssessments && errors.selectedCourses && (
                      <p className="text-[12px] text-red-500 mt-2">{errors.selectedAssessments}</p>
                    )}
                </SectionCard>

                {/* Footer */}
                <div className="flex items-center justify-between pt-2 pb-10">
                  <button type="button" onClick={() => navigate(`/app/subscription?tab=${fromTab}`)}
                    className="bg-transparent border-none text-[13px] font-medium text-gray-500 cursor-pointer">
                    Cancel
                  </button>
                  <div className="flex gap-[10px]">
                    <LoadingButton
                      type="submit"
                      onClick={() => { submitModeRef.current = 'draft'; }}
                      loading={submitting === 'draft'}
                      disabled={submitting === 'publish'}
                      variant="outlined"
                      sx={{ fontSize: '13px', fontWeight: 600, borderRadius: '8px', borderColor: '#d1d5db', color: '#374151', textTransform: 'none', px: '20px' }}
                    >
                      Save as Draft
                    </LoadingButton>

                    <LoadingButton
                      type="submit"
                      onClick={() => { submitModeRef.current = 'publish'; }}
                      loading={submitting === 'publish'}
                      disabled={submitting === 'draft'}
                      variant="contained"
                      sx={{ fontSize: '13px', fontWeight: 600, borderRadius: '8px', background: '#2563eb', textTransform: 'none', px: '20px', '&:hover': { background: '#1d4ed8' } }}
                    >
                      Publish Plan
                    </LoadingButton>
                  </div>
                </div>

              </Form>
            );
          }}
        </Formik>
      </div>
      <SnackBar open={snackbar.open} snackbarTitle={snackbar.title} close={() => setSnackbar({ open: false, title: '' })} />
    </Page>
  );
};

export default CreateB2BPlan;